import type { TokenType } from '@/types/intake';

/**
 * The human name of the IDENTIFIER SPACE a scanned or typed value was read in.
 *
 * WHY THIS EXISTS, and it is a control rather than a nicety. The bench user enters ONE value and
 * the server classifies it (`classifyToken`): eight digits is a kit barcode, five is an accession,
 * twelve or more is a carrier tracking number, and the rest is a member id. Those spaces OVERLAP.
 * Measured in 26fobt on 2026-09-06: 20,842 of 21,312 member ids are 6 or 7 characters wide, which
 * is exactly the width of 30 BUY-FIRST kit barcodes. So a value the technician read off a kit can
 * be looked up as a member, and when it resolves, the screen shows a confirmation card with a
 * DIFFERENT person's name and date of birth and nothing about it looks unusual.
 *
 * The API has always returned `tokenType`. Until 2026-09-07 the frontend read that field only to
 * notice a USPS scan and then discarded it, so the one fact that would reveal the misreading was
 * fetched and thrown away. Showing it is the fix Portal - QA asked for first, ahead of widening the
 * classifier, and their reasoning is the reason it comes first: a screen that STATES which space it
 * read is a control, where a classifier that guesses right is not. A classifier can only be right
 * more often; a person told what the machine decided can catch the case it got wrong.
 *
 * Deliberately NEUTRAL wording, with no warning styling and no "did you mean" hint. The overlap is
 * a fact about the two numbering schemes, not an error, and 20,799 six-character member ids are
 * legitimate lookups. Colouring them all as suspect would train the technician past the one that
 * matters, which is the same alarm-fatigue trap the cross-plan near-miss was narrowed to avoid.
 */
export const IDENTIFIER_SPACE_LABEL: Record<TokenType, string> = {
  'kit-barcode': 'kit barcode',
  accession: 'accession number',
  'usps-tracking': 'USPS tracking number',
  'kit-or-member': 'kit barcode or member ID',
  'member-id': 'member ID',
  'free-text': 'name or address search',
  'return-tail': 'last 5 digits of a return label',
};

/** The label for one token type, or a plain fallback for a type this build does not know. */
export function identifierSpaceLabel(t: TokenType | null | undefined): string {
  if (!t) return 'unrecognised value';
  return IDENTIFIER_SPACE_LABEL[t] ?? String(t);
}
